import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse, AuthResponse, LoginCredentials, RegisterCredentials, User } from '../types';
import { API_BASE_URL } from '../config/apiConfig';

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token过期，清除本地登录信息并回首页（路由表无 /login，跳 /login 会白屏）
      const hadToken = !!localStorage.getItem('token');
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      if (hadToken) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  // 注册
  register: async (credentials: RegisterCredentials): Promise<ApiResponse<AuthResponse>> => {
    const response = await api.post('/auth/register', credentials);
    return response.data;
  },

  // 登录
  login: async (credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  // 获取用户信息
  getUserInfo: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // 更新用户信息
  updateProfile: async (data: Partial<User>): Promise<ApiResponse<User>> => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },
};

// 游戏相关API（后端已实现）
export const gameAPI = {
  // 获取题目列表
  getStories: async (page = 1, limit = 20, category?: string, difficulty?: string): Promise<ApiResponse<any>> => {
    let url = `/game/list?page=${page}&limit=${limit}`;
    if (category) url += `&category=${category}`;
    if (difficulty) url += `&difficulty=${difficulty}`;
    const response = await api.get(url);
    return response.data;
  },

  // 获取题目详情
  getStoryDetail: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/game/${id}`);
    return response.data;
  },

  // 提交答案验证
  verifyAnswer: async (id: string, answer: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/game/${id}/verify`, { answer });
    return response.data;
  },

  // 每日挑战（当天全站同一题）
  getDaily: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/game/daily');
    return response.data;
  },
};

// AI相关API
export const aiAPI = {
  // AI问答
  ask: async (question: string, storyId: string, provider?: string, mode?: string): Promise<ApiResponse<any>> => {
    const response = await api.post('/ai/ask', { question, storyId, provider, mode });
    return response.data;
  },

  // AI对话（提示）
  chat: async (message: string, context: string, storyId: string, hintCount?: number, playerQuestions?: string[]): Promise<ApiResponse<any>> => {
    const response = await api.post('/ai/chat', { message, context, storyId, hintCount, playerQuestions });
    return response.data;
  },

  // AI复盘报告（游戏结束后）
  review: async (storyId: string, history: string[], solved: boolean): Promise<ApiResponse<any>> => {
    const response = await api.post('/ai/review', { storyId, history, solved }, { timeout: 30000 });
    return response.data;
  },

  // 获取AI提供商
  getProviders: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/ai/providers');
    return response.data;
  },
};

// 双线叙事API
export const dualStoryAPI = {
  // 获取列表
  getList: async (page = 1, limit = 10, category?: string, difficulty?: string): Promise<ApiResponse<any>> => {
    let url = `/dual-story?page=${page}&limit=${limit}`;
    if (category) url += `&category=${category}`;
    if (difficulty) url += `&difficulty=${difficulty}`;
    const response = await api.get(url);
    return response.data;
  },

  // 获取详情
  getDetail: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/dual-story/${id}`);
    return response.data;
  },

  // 开始游戏
  startGame: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/dual-story/${id}/start`);
    return response.data;
  },

  // 提交问题
  askQuestion: async (id: string, sessionId: string, question: string, timeline?: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/dual-story/${id}/question`, { sessionId, question, timeline });
    return response.data;
  },

  // 提交答案
  submitGuess: async (id: string, sessionId: string, modernGuess: string, ancientGuess: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/dual-story/${id}/guess`, { sessionId, modernGuess, ancientGuess });
    return response.data;
  },

  // 获取会话状态
  getSession: async (id: string, sessionId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/dual-story/${id}/session/${sessionId}`);
    return response.data;
  },
};

// 导师故事API
export const mentorStoryAPI = {
  // 获取列表
  getList: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/mentor-story');
    return response.data;
  },

  // 获取详情
  getDetail: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/mentor-story/${id}`);
    return response.data;
  },

  // 获取场景
  getScene: async (storyId: string, sceneId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/mentor-story/${storyId}/scene/${sceneId}`);
    return response.data;
  },

  // 处理选择
  handleChoice: async (storyId: string, sceneId: string, choiceIndex: number): Promise<ApiResponse<any>> => {
    const response = await api.post('/mentor-story/choice', { storyId, sceneId, choiceIndex });
    return response.data;
  },

  // 完成故事
  completeStory: async (storyId: string): Promise<ApiResponse<any>> => {
    const response = await api.post('/mentor-story/complete', { storyId });
    return response.data;
  },

  // 获取用户进度
  getProgress: async (storyId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/mentor-story/${storyId}/progress`);
    return response.data;
  },
};

export default api;

// 用户相关API
export const userAPI = {
  // 获取仪表盘
  getDashboard: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/user/dashboard');
    return response.data;
  },

  // 获取游戏历史
  getHistory: async (page = 1, limit = 15): Promise<ApiResponse<any>> => {
    const response = await api.get('/user/history', { params: { page, limit } });
    return response.data;
  },

  // 记录游戏结果
  recordGame: async (data: {
    storyId: string;
    storyTitle: string;
    userAnswer?: string;
    mode?: string;
    correct: boolean;
    questionsAsked?: number;
    hintsUsed?: number;
    duration?: number;
  }): Promise<ApiResponse<any>> => {
    const response = await api.post('/user/record-game', data);
    return response.data;
  },

  // 获取个人信息
  getProfile: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/user/profile');
    return response.data;
  },
};
